import React, { useRef, useState } from 'react';
import { List } from 'immutable';
import moment from 'moment';

import BaseModal from 'flow/components/ui-components/BaseModal';
import RadioButton from 'flow/components/ui-components/RadioButton';
import TimeInput from 'flow/components/ui-components/TimeInput';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Calendar, OpeningHour, OpeningHours } from 'flow/types/Calendar';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import {
    calculateBaseTime,
    isAllDay,
    setSameAsMonday,
    startsAtMidnight,
} from 'flow/utils/calendar-utils';

/**
 * Calculate the setting based on start+end date and time
 */
const getSelected = (openingHour: TypedMap<OpeningHour>) => {
    const start = openingHour.get('start');
    const end = openingHour.get('end');

    if (start === end) {
        return 'free_all_day';
    }

    if (isAllDay(moment(start), moment(end))) {
        return 'work_all_day';
    }

    return 'work_set_time';
};

interface Props {
    calendar?: TypedMap<Calendar>;
    profiles?: List<TypedMap<Profile>>;
    isExtension?: boolean;
    handleUpdateOpeningHours?: (
        updatedOpeningHours: TypedMap<Calendar>,
    ) => void;
    selectedDay?: 'monday-friday' | keyof OpeningHours;
}

const OpeningHourModal = ({
    calendar,
    profiles,
    handleUpdateOpeningHours,
    isExtension,
    selectedDay,
}: Props) => {
    const { i18next } = useAppContext();
    const { closeModal } = useModal();

    const timeFromRef = useRef<HTMLInputElement>(null);
    const timeEndRef = useRef<HTMLInputElement>(null);

    const activeDay: keyof OpeningHours =
        selectedDay === 'monday-friday' ? 'monday' : selectedDay;
    const openingHour = calendar.getIn(['openingHours', activeDay]);

    const startMoment = moment(openingHour.get('start'));
    const endMoment = moment(openingHour.get('end'));
    const bothMidnight =
        startsAtMidnight(startMoment) && startsAtMidnight(endMoment);

    const [selected, setSelected] = useState(getSelected(openingHour));
    const [defaultStartInput] = useState(
        bothMidnight ? '09:00' : startMoment.format('HH:mm'),
    );
    const [defaultEndInput] = useState(
        bothMidnight ? '17:00' : endMoment.format('HH:mm'),
    );

    const getProfileString = (plusID: number) =>
        `'${profiles
            .find(
                (profile: TypedMap<Profile>) =>
                    profile?.get('plusId') === plusID,
            )
            ?.get('description')}'`;

    const onSubmit = () => {
        const baseTime = calculateBaseTime(
            openingHour.get('start'),
            selectedDay,
        );

        const startTime = (
            selected === 'work_set_time' ? timeFromRef.current.value : '00:00'
        ).split(':');
        const endTime = (
            selected === 'work_set_time' ? timeEndRef.current.value : '00:00'
        ).split(':');

        const confStart = moment(baseTime)
            .hour(Number(startTime[0]))
            .minute(Number(startTime[1]))
            .second(0)
            .millisecond(0)
            .format(ISO_8601_MS_RESOLUTION);

        const confEnd = moment(baseTime)
            .hour(Number(endTime[0]))
            .minute(Number(endTime[1]))
            .second(0)
            .millisecond(0)
            .add(selected === 'work_all_day' ? 1 : 0, 'days')
            .format(ISO_8601_MS_RESOLUTION);

        const updated = openingHour.set('start', confStart).set('end', confEnd);

        updateOpeningHours(updated);
    };

    const updateOpeningHours = async (openingHour: TypedMap<OpeningHour>) => {
        const updatedOpeningHours =
            selectedDay === 'monday-friday'
                ? setSameAsMonday(calendar.get('openingHours'), openingHour)
                      .set(
                          'saturday',
                          calendar.getIn(['openingHours', 'saturday']),
                      )
                      .set('sunday', calendar.getIn(['openingHours', 'sunday']))
                : calendar.get('openingHours').set(selectedDay, openingHour);

        await handleUpdateOpeningHours(updatedOpeningHours);
        closeModal();
    };

    const allDay = i18next.t('calendar.all.day').toLowerCase();
    const between = i18next.t('calendar.pbx.between');

    const available = !profiles.isEmpty() && getProfileString(0);
    const leftForTheDay =
        !profiles.isEmpty() && getProfileString(3) + ' ' + allDay;

    return (
        <BaseModal
            open={true}
            header={i18next.t(selectedDay)}
            onSubmit={onSubmit}
        >
            <div className="flex flex-col gap-6 divide-y">
                <div className="pb-6">
                    <RadioButton
                        name="option"
                        checked={selected === 'free_all_day'}
                        onChange={() => setSelected('free_all_day')}
                    >
                        {isExtension ? leftForTheDay : i18next.t('pbx.closed')}
                    </RadioButton>
                    <RadioButton
                        name="option"
                        checked={selected === 'work_all_day'}
                        onChange={() => setSelected('work_all_day')}
                    >
                        {isExtension
                            ? available + ' ' + allDay
                            : i18next.t('pbx.open')}
                    </RadioButton>
                    <RadioButton
                        name="option"
                        checked={selected === 'work_set_time'}
                        onChange={() => setSelected('work_set_time')}
                    >
                        {isExtension
                            ? `${available} ${between}`
                            : i18next.t('calendar.pbx.set.time')}
                    </RadioButton>
                </div>

                {selected === 'work_set_time' && (
                    <div className="flex flex-col gap-3">
                        <TimeInput
                            label={i18next.t('from')}
                            ref={timeFromRef}
                            defaultValue={defaultStartInput}
                        />
                        <TimeInput
                            label={i18next.t('to')}
                            ref={timeEndRef}
                            defaultValue={defaultEndInput}
                        />
                    </div>
                )}
            </div>
        </BaseModal>
    );
};

export default OpeningHourModal;
